import React, { useState, useEffect, useRef } from 'react';
import { sanitizeHtml } from '../../../../../Utils/sanitize';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-swift';
import 'prismjs/components/prism-http';
import 'prismjs/themes/prism-tomorrow.css';
import styles from './CodeSnippet.module.scss';

const languageMap = {
  curl: 'bash',
  shell: 'bash',
  javascript: 'javascript',
  node: 'javascript',
  python: 'python',
  html: 'markup',
  xml: 'markup',
  c: 'c',
  cpp: 'cpp',
  java: 'java',
  php: 'php',
  csharp: 'csharp',
  swift: 'swift',
  http: 'http',
};

const CodeSnippet = ({ examples, language, hasScroll = false }) => {
  const [code, setCode] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (examples && language && examples[language]) {
      setCode(examples[language]);
    } else if (examples) {
      const firstLanguage = Object.keys(examples)[0];
      if (firstLanguage) {
        setCode(examples[firstLanguage] || '');
      }
    }
  }, [language, examples]);

  useEffect(() => {
    if (editorRef.current) {
      const highlightedElement = editorRef.current.querySelector(
        `.${CSS.escape(styles.highlight)}`
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [code]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
  };

  const highlightCode = (code) => {
    const prismLanguage = languageMap[language] || "javascript";
    if (languages[prismLanguage]) {
      const START_TOKEN = `__HIGHLIGHT_START__`;
      const END_TOKEN = `__HIGHLIGHT_END__`;

      let highlightedCode = highlight(code, languages[prismLanguage], prismLanguage);
      
      const startRegex = new RegExp(START_TOKEN.split('').join('(.*?)'), 'g');
      const endRegex = new RegExp(END_TOKEN.split('').join('(.*?)'), 'g');

      highlightedCode = highlightedCode
        .replace(startRegex, `<div class="${styles.highlight}">`)
        .replace(endRegex, "</div>");

      return highlightedCode;
    }
    return code; // No highlighting if language is not loaded
  };

  return (
    <div
      className={`${styles.codeSnippet} ${hasScroll ? styles.withScroll : ""}`}
      ref={editorRef}
    >
      <div className={styles.codeSnippetHeader}>
        <button className={styles.copyButton} onClick={handleCopy}>
          {isCopied ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          )}
        </button>
      </div>
      <div className={styles.editorContainer}>
        <pre
          className={styles.pre}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(highlightCode(code)) }}
        />
      </div>
    </div>
  );
};

export default CodeSnippet;